<script setup lang="ts">
import { storeToRefs } from 'pinia';
import LoyaltyHubSheet from './Sheet.vue';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';

// A LISTAGEM do Blog aberta DENTRO de um modal — gêmeo do PromotionsSheet, e o
// mesmo desenho da loja no StoreSheet. A ROTA /blog CONTINUA existindo e igual:
// aqui reaproveitamos a mesma `<BaseBlogCore />`, em modo embutido
// (`embedded`), com as abas de categoria e tudo; lista e post acontecem no MESMO
// painel e o voltar da tela devolve pra listagem.
//
// Quem abre é o deep-link `?open=blog` (ou `?open=blog&slug=…`, que já cai no
// post). Não substitui o PostSheet: aquele é a promoção do blog aberta SOZINHA,
// que é o que os cartões do hub usam (`?open=post&slug=…`).
//
// Estado só em memória (nada na URL), igual roletinha e cashback.
const store = useLoyaltyHubStore();
const { isBlogOpen, blogSlug } = storeToRefs(store);

// Monta só depois do slide-up: a listagem busca todas as categorias do CMS e
// montar durante a animação faz a subida engasgar. Mesma estratégia da loja.
const mounted = ref(false);

const sheetRef = ref<InstanceType<typeof LoyaltyHubSheet> | null>(null);

// Fechar e reabrir DURANTE o slide-down cancela a saída: o `@closed`
// (after-leave) nunca dispara, o `mounted` não volta pra false e a tela
// continuaria montada com o post anterior aberto. A chave por abertura força a
// remontagem, que é o que devolve o painel pra listagem.
const openCount = ref(0);

watch(isBlogOpen, (open) => {
  if (open) openCount.value += 1;
});

function close() {
  store.closeBlog();
}

// Troca de tela dentro do painel: quem rola é o sheet, então é ele que sobe.
function onNavigate() {
  sheetRef.value?.scrollToTop();
}
</script>

<template>
  <LoyaltyHubSheet
    ref="sheetRef"
    desktop-max-width="1000px"
    :open="isBlogOpen"
    @close="close"
    @closed="mounted = false"
    @opened="mounted = true"
  >
    <!-- Wrapper meu, e NÃO `class` direto na `<BaseBlogCore>`: raiz de fragmento
         não herda classe — mesmo caso do StoreSheet. -->
    <div
      v-if="mounted"
      class="lhbl-blog"
    >
      <BaseBlogCore
        :key="openCount"
        embedded
        :slug="blogSlug"
        @close="close"
        @navigate="onNavigate"
      />
    </div>

    <!-- Enquanto o painel sobe, o vão fica com o esqueleto da lista. -->
    <div
      v-else
      class="lhbl-loading"
    >
      <div
        v-for="i in 3"
        :key="`lhbl-skeleton-${i}`"
        class="lhbl-skeleton"
      ></div>
    </div>
  </LoyaltyHubSheet>
</template>

<style scoped lang="scss">
// A tela se desenha pra ser a PÁGINA: ela sobe o próprio topo (`-mt-4 lg:-mt-5`)
// pra compensar a folga do header do site, que aqui não existe. O alvo é por
// posição (`> *:first-child`), e não pela classe, pelo mesmo motivo do
// StoreSheet.
.lhbl-blog {
  width: 100%;

  :deep(> *:first-child) {
    margin-top: 0 !important;
  }
}

.lhbl-loading {
  display: grid;
  gap: 12px;
}

.lhbl-skeleton {
  @apply w-full h-40 rounded-lg bg-texts/5 border border-solid border-texts/10;
}
</style>
